import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert } from 'components/Alert'
import {
  formatResultCount,
  ManagementDateRangeFilter,
  ManagementFilterBar,
  ManagementPagination,
  ManagementSearchInput,
  ManagementMultiSelectFilter,
  ManagementSelectFilter,
} from 'components/ManagementFilters'
import { Snackbar } from 'components/Snackbar'
import { pageCardClasses } from 'components/AppLayout/styles'
import { USERS_PAGE_SIZE } from 'constants/index'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { commonStrings, userStrings } from 'constants/strings'
import {
  useAdCodeOptions,
  useCurrentUser,
  useLineFriends,
  useTags,
  useUpdateLineFriendTestUser,
} from 'hooks'
import { UsersCardList } from 'pages/Users/UsersCardList'
import { UsersTable } from 'pages/Users/UsersTable'
import { readFriendStatusFromSearch } from 'pages/Users/lineFriendView'
import { EOrganizationFeature } from 'types/enum'
import type { TLineFriendStatus } from 'types'
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature'

type UsersLocationState = {
  snackbarMessage?: string
}

type FriendStatusFilter = TLineFriendStatus

const friendStatusFilterOptions = [
  { value: 'NORMAL', label: userStrings.table.friendStatus.normal },
  { value: 'BLOCKED', label: userStrings.table.friendStatus.blocked },
  {
    value: 'NO_LINKED_CHANNEL',
    label: userStrings.table.friendStatus.noLinkedChannel,
  },
] satisfies { value: FriendStatusFilter; label: string }[]

const testUserFilterOptions = [
  { value: 'true', label: userStrings.filters.testUserOnly },
] satisfies { value: 'true'; label: string }[]

/** タグ絞り込みの「タグなし」を表す値。タグIDと衝突しない文字列にする */
const UNTAGGED_TAG_FILTER = 'untagged'

export default function Users() {
  const location = useLocation()
  const navigate = useNavigate()
  const { data: currentUser } = useCurrentUser()
  const canUseTags = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.TAG_MANAGEMENT,
  )
  const orgPathFor = useOrgPath()
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  // 友だち分析などから ?friendStatus= 付きで開かれたら、その絞り込みから始める。
  // URLは初期値として一度読むだけで、画面での変更は書き戻さない
  const [friendStatusFilter, setFriendStatusFilter] = useState<
    FriendStatusFilter | ''
  >(() => readFriendStatusFromSearch(location.search))
  const [tagFilter, setTagFilter] = useState('')
  const [lastAccessRange, setLastAccessRange] = useState({ from: '', to: '' })
  const [friendRegisteredRange, setFriendRegisteredRange] = useState({
    from: '',
    to: '',
  })
  const [testUserFilter, setTestUserFilter] = useState<'' | 'true'>('')
  const [adCodeFilter, setAdCodeFilter] = useState<{
    selected: number[]
    mode: 'only' | 'except'
    noneSelected: boolean
  }>({ selected: [], mode: 'only', noneSelected: false })
  const filterResetKey = JSON.stringify({
    searchKeyword,
    friendStatusFilter,
    tagFilter,
    lastAccessRange,
    friendRegisteredRange,
    testUserFilter,
    // 実際に取得条件へ載る形だけをキーにする。何も選ばずに「のみ／以外」を
    // 切り替えただけでページが戻らないようにするため。
    adCodeFilter: adCodeFilter.noneSelected
      ? { noneSelected: true }
      : adCodeFilter.selected.length
        ? { selected: adCodeFilter.selected, mode: adCodeFilter.mode }
        : null,
  })
  const [pagination, setPagination] = useState({
    page: 1,
    resetKey: filterResetKey,
  })
  // フィルタが変わった瞬間に render 中で 1 ページ目へ同期リセットし、
  // 古い page での無駄な fetch を防ぐ（useClientPagination と同じ手法）
  const page = Object.is(pagination.resetKey, filterResetKey)
    ? pagination.page
    : 1
  const { friends, total, totalAll, totalPage, loading, error } =
    useLineFriends({
      currentPage: page,
      pageSize: USERS_PAGE_SIZE,
      search: searchKeyword,
      friendStatus: friendStatusFilter,
      // 「タグなし」は tagId ではなく専用フラグで送る（tagId=0 は API 側で無視されるため）
      tagId:
        canUseTags && tagFilter && tagFilter !== UNTAGGED_TAG_FILTER
          ? Number(tagFilter)
          : undefined,
      isUntagged:
        canUseTags && tagFilter === UNTAGGED_TAG_FILTER ? true : undefined,
      lastAccessFrom: lastAccessRange.from,
      lastAccessTo: lastAccessRange.to,
      friendRegisteredFrom: friendRegisteredRange.from,
      friendRegisteredTo: friendRegisteredRange.to,
      isTestUser: testUserFilter === 'true' ? true : undefined,
      // 「流入元なし」と個別指定は排他。片方だけを送る
      hasNoAdCode: adCodeFilter.noneSelected ? true : undefined,
      adCodeIds:
        !adCodeFilter.noneSelected && adCodeFilter.selected.length
          ? adCodeFilter.selected
          : undefined,
      adCodeMode:
        !adCodeFilter.noneSelected && adCodeFilter.selected.length
          ? adCodeFilter.mode
          : undefined,
    })
  const {
    toggle: toggleTestUser,
    pendingUserLineIds: pendingTestUserIds,
    error: testUserError,
  } = useUpdateLineFriendTestUser()

  useEffect(() => {
    if (loading || error) {
      return
    }

    if (!Object.is(pagination.resetKey, filterResetKey)) {
      setPagination({ page: 1, resetKey: filterResetKey })
      return
    }

    if (totalPage === 0 && page !== 1) {
      setPagination({ page: 1, resetKey: filterResetKey })
      return
    }

    if (totalPage > 0 && page > totalPage) {
      setPagination({ page: totalPage, resetKey: filterResetKey })
    }
  }, [error, loading, page, totalPage, filterResetKey, pagination.resetKey])

  useEffect(() => {
    const state = location.state as UsersLocationState | null
    const message = state?.snackbarMessage
    if (!message) return

    setSnackbarMessage(message)
    setIsSnackbarOpen(true)
    navigate(orgPathFor(routes.USERS), { replace: true, state: null })
  }, [location.state, navigate, orgPathFor])

  const { tags } = useTags({
    enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT),
  })
  const adCodeOptions = useAdCodeOptions()
  const tagFilterOptions = useMemo(
    () => [
      { value: UNTAGGED_TAG_FILTER, label: userStrings.filters.untagged },
      ...tags.map((tag) => ({
        value: String(tag.id),
        label: tag.name,
      })),
    ],
    [tags],
  )

  const isFiltering = Boolean(
    searchKeyword ||
      friendStatusFilter ||
      tagFilter ||
      lastAccessRange.from ||
      lastAccessRange.to ||
      friendRegisteredRange.from ||
      friendRegisteredRange.to ||
      testUserFilter ||
      adCodeFilter.noneSelected ||
      adCodeFilter.selected.length > 0,
  )
  const countLabel = formatResultCount(total, totalAll)
  const emptyMessage = isFiltering
    ? commonStrings.filteredEmpty
    : userStrings.states.empty

  const handlePrev = () => {
    if (loading || totalPage === 0 || page <= 1) {
      return
    }

    setPagination({ page: Math.max(1, page - 1), resetKey: filterResetKey })
  }

  const handleNext = () => {
    if (loading || totalPage === 0 || page >= totalPage) {
      return
    }

    setPagination({ page: page + 1, resetKey: filterResetKey })
  }

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {userStrings.title}
            </h1>
            <p className="max-w-2xl text-xs text-muted">
              {userStrings.countDescription}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
            <ManagementPagination
              currentPage={page}
              totalPage={totalPage}
              loading={loading}
              onPrevious={handlePrev}
              onNext={handleNext}
            />
          </div>
        </header>

        {error && (
          <div className="mt-6">
            <Alert>{error}</Alert>
          </div>
        )}

        {testUserError && (
          <div className="mt-6">
            <Alert>{testUserError}</Alert>
          </div>
        )}

        <ManagementFilterBar>
          <ManagementSearchInput
            label={commonStrings.filters.searchLabel}
            placeholder={userStrings.filters.searchPlaceholder}
            value={searchKeyword}
            onChange={setSearchKeyword}
            onClear={() => setSearchKeyword('')}
          />
          <ManagementSelectFilter
            label={userStrings.filters.friendStatus}
            value={friendStatusFilter}
            options={friendStatusFilterOptions}
            onChange={setFriendStatusFilter}
            onClear={() => setFriendStatusFilter('')}
          />
          {canUseTags && (
            <ManagementSelectFilter
              label={userStrings.filters.tag}
              value={tagFilter}
              options={tagFilterOptions}
              onChange={setTagFilter}
              onClear={() => setTagFilter('')}
            />
          )}
          <ManagementMultiSelectFilter
            label={userStrings.filters.adCode}
            options={adCodeOptions}
            selected={adCodeFilter.selected}
            mode={adCodeFilter.mode}
            noneSelected={adCodeFilter.noneSelected}
            noneLabel={userStrings.filters.adCodeNone}
            searchPlaceholder={userStrings.filters.adCodeSearch}
            onlyLabel={userStrings.filters.adCodeOnly}
            exceptLabel={userStrings.filters.adCodeExcept}
            allLabel={commonStrings.filters.all}
            selectedSummary={(count) =>
              `${count}${userStrings.filters.adCodeSelected}`
            }
            onChange={setAdCodeFilter}
          />
          <ManagementSelectFilter
            label={userStrings.filters.testUser}
            value={testUserFilter}
            options={testUserFilterOptions}
            onChange={setTestUserFilter}
            onClear={() => setTestUserFilter('')}
          />
          <ManagementDateRangeFilter
            label={userStrings.filters.lastAccessAt}
            from={lastAccessRange.from}
            to={lastAccessRange.to}
            onChange={setLastAccessRange}
            onClear={() => setLastAccessRange({ from: '', to: '' })}
          />
          <ManagementDateRangeFilter
            label={userStrings.filters.friendRegisteredAt}
            from={friendRegisteredRange.from}
            to={friendRegisteredRange.to}
            onChange={setFriendRegisteredRange}
            onClear={() => setFriendRegisteredRange({ from: '', to: '' })}
          />
        </ManagementFilterBar>

        <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white lg:block table-scroll">
          <UsersTable
            users={friends}
            loading={loading}
            emptyMessage={emptyMessage}
            onToggleTestUser={toggleTestUser}
            pendingTestUserIds={pendingTestUserIds}
          />
        </div>
        <div className="mt-6 lg:hidden">
          <UsersCardList
            users={friends}
            loading={loading}
            emptyMessage={emptyMessage}
            onToggleTestUser={toggleTestUser}
            pendingTestUserIds={pendingTestUserIds}
            className="md:grid-cols-2"
          />
        </div>
      </section>
      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        variant="success"
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </>
  )
}
